'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ApiResult, WriteTextResult } from './api';
import {
  deleteFile as apiDeleteFile,
  listFiles as apiListFiles,
  resolveWorkspace as apiResolveWorkspace,
  snapshotFromSandbox as apiSnapshot,
  uploadFile as apiUpload,
  writeTextFile as apiWriteText,
} from './api';
import type { SnapshotResult, UploadResult, Workspace, WorkspaceFileEntry } from './types';

export type LoadPhase = 'idle' | 'loading' | 'ready' | 'unavailable' | 'error';

interface WorkspaceContextValue {
  sessionId: string | null;
  workspace: Workspace | null;
  resolvePhase: LoadPhase;
  resolveError: string | null;

  files: WorkspaceFileEntry[];
  filesPhase: LoadPhase;
  filesError: string | null;

  selectedPath: string | null;
  selectFile: (path: string | null) => void;
  selectedFile: WorkspaceFileEntry | null;

  retryResolve: () => void;
  refreshFiles: () => void;

  upload: (file: File, path: string) => Promise<ApiResult<UploadResult>>;
  remove: (path: string) => Promise<ApiResult<{ deleted: boolean; path: string }>>;
  snapshot: () => Promise<ApiResult<SnapshotResult>>;
  /**
   * Save text through the canonical write path. `baseVersion` = the version the
   * editor loaded from (omit to force-overwrite). Refreshes the file list on
   * success so the tree/version reflects the new head.
   */
  writeText: (
    path: string,
    textContent: string,
    mime?: string,
    baseVersion?: number,
  ) => Promise<WriteTextResult>;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be used within <WorkspaceProvider>');
  return ctx;
}

export function WorkspaceProvider({
  sessionId,
  children,
}: {
  sessionId: string | null;
  children: React.ReactNode;
}) {
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [resolvePhase, setResolvePhase] = useState<LoadPhase>('idle');
  const [resolveError, setResolveError] = useState<string | null>(null);

  const [files, setFiles] = useState<WorkspaceFileEntry[]>([]);
  const [filesPhase, setFilesPhase] = useState<LoadPhase>('idle');
  const [filesError, setFilesError] = useState<string | null>(null);

  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [resolveNonce, setResolveNonce] = useState(0);
  const [filesNonce, setFilesNonce] = useState(0);

  // ---- Resolve the workspace from the session id ----
  useEffect(() => {
    if (!sessionId) {
      setResolvePhase('unavailable');
      setResolveError('No active session — open or start a chat to get a workspace.');
      setWorkspace(null);
      return;
    }
    let cancelled = false;
    setResolvePhase('loading');
    setResolveError(null);
    apiResolveWorkspace(sessionId).then((res) => {
      if (cancelled) return;
      if (res.ok) {
        setWorkspace(res.data.workspace);
        setResolvePhase('ready');
      } else if (res.notAvailable) {
        setResolvePhase('unavailable');
        setResolveError('Workspace storage is not available yet.');
      } else {
        setResolvePhase('error');
        setResolveError(res.error);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [sessionId, resolveNonce]);

  // ---- Load the flat file list once the workspace resolves ----
  useEffect(() => {
    if (resolvePhase !== 'ready' || !workspace) return;
    let cancelled = false;
    setFilesPhase('loading');
    setFilesError(null);
    apiListFiles(workspace.id).then((res) => {
      if (cancelled) return;
      if (res.ok) {
        setFiles(res.data.files ?? []);
        setFilesPhase('ready');
      } else if (res.notAvailable) {
        setFilesPhase('unavailable');
        setFilesError('Workspace storage is not available yet.');
      } else {
        setFilesPhase('error');
        setFilesError(res.error);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [resolvePhase, workspace, filesNonce]);

  const retryResolve = useCallback(() => setResolveNonce((n) => n + 1), []);
  const refreshFiles = useCallback(() => setFilesNonce((n) => n + 1), []);

  const selectFile = useCallback((path: string | null) => setSelectedPath(path), []);

  const selectedFile = useMemo(
    () => files.find((f) => f.path === selectedPath) ?? null,
    [files, selectedPath],
  );

  const upload = useCallback(
    async (file: File, path: string) => {
      if (!workspace) {
        return {
          ok: false as const,
          status: 0,
          error: 'No workspace resolved',
          notAvailable: true,
        };
      }
      const res = await apiUpload(workspace.id, file, path);
      if (res.ok) refreshFiles();
      return res;
    },
    [workspace, refreshFiles],
  );

  const remove = useCallback(
    async (path: string) => {
      if (!workspace) {
        return {
          ok: false as const,
          status: 0,
          error: 'No workspace resolved',
          notAvailable: true,
        };
      }
      const res = await apiDeleteFile(workspace.id, path);
      if (res.ok) {
        if (selectedPath === path) setSelectedPath(null);
        refreshFiles();
      }
      return res;
    },
    [workspace, refreshFiles, selectedPath],
  );

  const snapshot = useCallback(async () => {
    if (!workspace) {
      return {
        ok: false as const,
        status: 0,
        error: 'No workspace resolved',
        notAvailable: true,
      };
    }
    const res = await apiSnapshot(workspace.id);
    if (res.ok) refreshFiles();
    return res;
  }, [workspace, refreshFiles]);

  const writeText = useCallback(
    async (path: string, textContent: string, mime?: string, baseVersion?: number) => {
      if (!workspace) {
        return {
          ok: false as const,
          conflict: false as const,
          status: 0,
          error: 'No workspace resolved',
          notAvailable: true,
        };
      }
      const res = await apiWriteText(workspace.id, path, textContent, mime, baseVersion);
      if (res.ok) refreshFiles();
      return res;
    },
    [workspace, refreshFiles],
  );

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      sessionId,
      workspace,
      resolvePhase,
      resolveError,
      files,
      filesPhase,
      filesError,
      selectedPath,
      selectFile,
      selectedFile,
      retryResolve,
      refreshFiles,
      upload,
      remove,
      snapshot,
      writeText,
    }),
    [
      sessionId,
      workspace,
      resolvePhase,
      resolveError,
      files,
      filesPhase,
      filesError,
      selectedPath,
      selectFile,
      selectedFile,
      retryResolve,
      refreshFiles,
      upload,
      remove,
      snapshot,
      writeText,
    ],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}
